import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';

import { env } from '@/shell/env';
import { rootGuards } from '@/shell/auth/guards';
import { aggregateFeatureRoutes } from '@/shell/feature-registry';

export function createShellRouter() {
  const featureRoutes = aggregateFeatureRoutes();
  const routes: RouteRecordRaw[] = [
    {
      path: '/',
      redirect: () => `/${env.devFallbackSlug}/items`,
    },
    ...featureRoutes,
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/shell/components/NotFound.vue'),
    },
  ];

  const router = createRouter({
    history: createWebHistory(),
    routes,
  });

  // App scroll lives inside .app-shell__body (not window), so Vue
  // Router's built-in scrollBehavior doesn't reach it. We do it manually:
  // — Forward push: land at top of the new page.
  // — Back pop: restore the scroll position we saved when leaving.
  // — Same path (only the query changed): leave scroll alone. That is how
  //   overlays open and close (`?bid=` drives the bid sheet), and the page
  //   behind them must stay where the bidder was. A page that does want the
  //   top on a query change (catalog filters) scrolls there itself.
  // Direction is detected via history.state.position, the index Vue Router
  // stamps on every entry.
  const scrollByPath = new Map<string, number>();
  let prevIndex: number =
    typeof window !== 'undefined'
      ? ((window.history.state?.position as number | undefined) ?? 0)
      : 0;

  router.beforeEach((_to, from) => {
    if (typeof document === 'undefined' || !from.path) return;
    const body = document.querySelector<HTMLElement>('.app-shell__body');
    if (body) scrollByPath.set(from.path, body.scrollTop);
  });

  // Bumped by every navigation so a restore still retrying for the previous
  // route stops instead of dragging the new one.
  let restoreToken = 0;

  router.afterEach((to, from) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }
    const currentIndex =
      (window.history.state?.position as number | undefined) ?? 0;
    const isBack = currentIndex < prevIndex;
    prevIndex = currentIndex;
    if (from.matched.length > 0 && to.path === from.path) return;

    const target = isBack ? (scrollByPath.get(to.path) ?? 0) : 0;
    const token = ++restoreToken;
    const body = document.querySelector<HTMLElement>('.app-shell__body');
    if (!body) return;

    // Asserted frame by frame rather than set once. The new view mounts and
    // lays out on its own schedule (later still under an iOS back-swipe),
    // and an assignment made while the content is too short to reach the
    // target is silently clamped. Done once the position has held for two
    // consecutive frames, on the deadline, or the moment the bidder scrolls
    // for themselves.
    const deadline = performance.now() + 1000;
    let userTookOver = false;
    let heldFrames = 0;
    const takeOver = (): void => {
      userTookOver = true;
    };
    const userInputs = ['wheel', 'touchstart', 'keydown'] as const;
    for (const type of userInputs) body.addEventListener(type, takeOver, { passive: true });
    const finish = (): void => {
      for (const type of userInputs) body.removeEventListener(type, takeOver);
    };
    const attempt = (): void => {
      if (token !== restoreToken || userTookOver) return finish();
      body.scrollTop = target;
      heldFrames = Math.abs(body.scrollTop - target) <= 1 ? heldFrames + 1 : 0;
      if (heldFrames >= 2 || performance.now() >= deadline) return finish();
      requestAnimationFrame(attempt);
    };
    requestAnimationFrame(attempt);
  });

  router.onError((error, to) => {
    const msg = error instanceof Error ? error.message : String(error);
    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed')
    ) {
      const key = `bidder-web:chunk-reload:${to.fullPath}`;
      if (sessionStorage.getItem(key) === '1') return;
      sessionStorage.setItem(key, '1');
      window.location.assign(to.fullPath);
    }
  });

  rootGuards(router);
  return router;
}
